import {
  isAssetReadOnly,
  Media,
  useCurrentAccount,
  useProduct,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import { LoadingIndicator, MediaListItem } from '@gripp/shared-ui';
import { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useNavigate } from 'react-router-dom';
import { AddFilesLinksSheet } from './addFilesLinksButton';
import { MediaExtended } from './filesContext';
import { filesSharedStyles } from './filesSharedStyles';

type FileRightColumnProps = {
  assetId: string;
  searchKeyword: string;
  selectedCategoryId: string | undefined;
  onAllFilesLoaded: (files: MediaExtended[]) => void;
};

export const FileRightColumn = ({
  assetId,
  searchKeyword,
  selectedCategoryId,
  onAllFilesLoaded,
}: FileRightColumnProps) => {
  const { workspaceAsset, invalidateQuery, isLoading } = useWorkspaceAsset(assetId);
  const asset = workspaceAsset?.asset;
  const { account } = useCurrentAccount();
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const navigate = useNavigate();

  const { product } = useProduct(asset?.product?.id || '');

  const allFiles = useMemo(() => {
    const files: MediaExtended[] = [];

    const productMediaIds = new Set(
      product?.media?.map((m: Media) => m.id) || []
    );

    if (asset?.media) {
      asset.media.forEach((media: Media) => {
        const isFromProduct = productMediaIds.has(media.id);
        files.push({ ...media, isFromProduct });
      });
    }

    onAllFilesLoaded(files);
    return files;
  }, [asset?.media, product?.media, onAllFilesLoaded]);

  const filteredFiles = useMemo(() => {
    let files = allFiles;

    if (selectedCategoryId) {
      files = files.filter((file) =>
        file.categories?.some((cat) => cat.id === selectedCategoryId)
      );
    }

    if (searchKeyword.trim()) {
      const query = searchKeyword.toLowerCase();
      files = files.filter((file) => file.name?.toLowerCase().includes(query));
    }

    return files;
  }, [allFiles, searchKeyword, selectedCategoryId]);

  const handleMediaClick = useCallback(
    (media: Media) => {
      navigate(`/assets/${assetId}/files/${media.id}`);
    },
    [assetId, navigate]
  );

  if (isLoading) {
    return (
      <View style={filesSharedStyles.container}>
        <LoadingIndicator flexContainer />
      </View>
    );
  }

  if (!workspaceAsset) return null;

  return (
    <View style={filesSharedStyles.container}>
      <ScrollView
        showsVerticalScrollIndicator={true}
        style={filesSharedStyles.scrollView}
      >
        {filteredFiles.map((file: MediaExtended) => (
          <MediaListItem
            key={file.id}
            media={file}
            invalidateQuery={invalidateQuery}
            useNewCropCircle
            onClickMedia={handleMediaClick}
          />
        ))}
      </ScrollView>
      {asset && !isReadOnlyMode && (
        <AddFilesLinksSheet
          asset={asset}
          invalidateQuery={invalidateQuery}
        />
      )}
    </View>
  );
};
